import { EPreviewSections } from '../types/admin';
import {
  ELandingTabsEnum,
  MobileTabsEnum,
  TPolygon,
  ETimelineStepStatus,
  TProject,
} from '../types/config';
import { projectUtils, publicAssets } from '../utils';
import {
  balconyIcon,
  cubeIcon,
  featherAvatar,
  filtersIcon,
  floorplanIcon,
  phoneIcon,
  starAvatar,
} from './icons';

export const landingTabsToPreviewSection = {
  [ELandingTabsEnum.About]: EPreviewSections.ProjectDescription,
  [ELandingTabsEnum.InteractiveView]: EPreviewSections.InteractiveView,
  [ELandingTabsEnum.Promotions]: EPreviewSections.Promotions,
  [ELandingTabsEnum.Specifications]: EPreviewSections.Specifications,
  [ELandingTabsEnum.Gallery]: EPreviewSections.ProjectGallery,
  [ELandingTabsEnum.Map]: EPreviewSections.Address,
  [ELandingTabsEnum.Inventory]: EPreviewSections.UnitApartmentsSpecs,
};

// export const previewSectionToLandingTabs = Object.fromEntries(
//   Object.entries(landingTabsToPreviewSection).map(([key, value]) => [value, key])
// );

export const getNavbarPages = (
  t: (key: string) => string,
  project: TProject,
  apartmentLanding?: TPolygon,
) => {
  const navbarPages = [];

  if (projectUtils.hasDescription(project)) {
    navbarPages.push({
      key: ELandingTabsEnum.About,
      label: t('labels.about'),
      previewSectionsKey: landingTabsToPreviewSection[ELandingTabsEnum.About],
    });
  }

  // interactive tab had own tabs switch scenarios !
  // {
  //   key: ELandingTabsEnum.Interactive,
  //   label: t('labels.interactive'),
  // },

  if (projectUtils.hasInteractive(project)) {
    navbarPages.push({
      key: ELandingTabsEnum.InteractiveView,
      label: t('labels.3Dtours'),
      previewSectionsKey: landingTabsToPreviewSection[ELandingTabsEnum.InteractiveView],
    });
  }

  if (!!apartmentLanding?.tourUrl) {
    navbarPages.push({
      key: ELandingTabsEnum.InteractiveView,
      label: t('labels.interactiveView'),
    });
  }

  if (projectUtils.hasPromotions(project)) {
    navbarPages.push({
      key: ELandingTabsEnum.Promotions,
      label: t('labels.promotions'),
      previewSectionsKey: landingTabsToPreviewSection[ELandingTabsEnum.Promotions],
    });
  }

  if (projectUtils.hasSpecifications(project)) {
    navbarPages.push({
      key: ELandingTabsEnum.Specifications,
      label: t('labels.specifications'),
      previewSectionsKey: landingTabsToPreviewSection[ELandingTabsEnum.Specifications],
    });
  }

  if (projectUtils.hasGallery(project)) {
    navbarPages.push({
      key: ELandingTabsEnum.Gallery,
      label: t('labels.gallery'),
      previewSectionsKey: landingTabsToPreviewSection[ELandingTabsEnum.Gallery],
    });
  }

  navbarPages.push({
    key: ELandingTabsEnum.Map,
    label: t('labels.map'),
    previewSectionsKey: landingTabsToPreviewSection[ELandingTabsEnum.Map],
  });

  if (projectUtils.hasInventory(project) && !apartmentLanding) {
    navbarPages.push({
      key: ELandingTabsEnum.Inventory,
      label: t('labels.inventory'),
      previewSectionsKey: landingTabsToPreviewSection[ELandingTabsEnum.Inventory],
    });
  }

  return navbarPages;
};

export const getApartmentMobileTabs = (
  t: (key: string) => string,
  hasTour: boolean,
  withAptDetails = true,
  withFloorPlan = true,
  withLeadForm = false, // replaces withAptDetails flow
  withBalcony = true,
) => [
  ...(hasTour
    ? [{ key: MobileTabsEnum.ThreeDTour, icon: cubeIcon, label: t('labels.3Dtour') }]
    : []),
  ...(withAptDetails
    ? [
        {
          key: MobileTabsEnum.ApartmentDetails,
          icon: filtersIcon,
          label: t('labels.aptDetails'),
        },
      ]
    : []),
  ...(withFloorPlan
    ? [
        {
          key: MobileTabsEnum.FloorPlan,
          icon: floorplanIcon,
          label: t('labels.floorPlan'),
        },
      ]
    : []),
  ...(withLeadForm
    ? [
        {
          key: MobileTabsEnum.LeadForm,
          icon: phoneIcon,
          label: t('labels.contactUsTab'),
        },
      ]
    : []),
  ...(withBalcony
    ? [
        {
          key: MobileTabsEnum.Balcony,
          icon: balconyIcon,
          label: t('labels.balconyView'),
        },
      ]
    : []),
];

export const getPillsNavbarData = (
  t: (key: string, options?: { v: number }) => string,
  yadFullProjects: number[],
  project: TProject,
  apartmentLanding?: TPolygon,
) => {
  const tabs = [];

  if (projectUtils.hasInventory(project) && !apartmentLanding) {
    tabs.push({
      key: ELandingTabsEnum.Inventory,
      src: publicAssets('shared/inventory_avatar.webp'),
      title: t('labels.inventory'),
      description: t('info.inventoryDescription'),
      show3DBadge: project.listings?.some((p) => !!p.tourUrl),
      previewSectionsKey: landingTabsToPreviewSection[ELandingTabsEnum.Inventory],
    });
  }

  tabs.push({
    key: ELandingTabsEnum.Map,
    src: publicAssets('shared/map_avatar.webp'),
    title: t('labels.nearbyPlaces'),
    description: t('labels.checkInMinutes', { v: 20 }),
    previewSectionsKey: landingTabsToPreviewSection[ELandingTabsEnum.Map],
  });

  if (projectUtils.hasPromotions(project)) {
    tabs.push({
      key: ELandingTabsEnum.Promotions,
      src: publicAssets('shared/special_offers_avatar.webp'),
      title: t('labels.promotions'),
      description: t('labels.offersAvailable', { v: 2 }),
      previewSectionsKey: landingTabsToPreviewSection[ELandingTabsEnum.Promotions],
    });
  }

  if (projectUtils.hasInteractive(project)) {
    tabs.push({
      key: ELandingTabsEnum.InteractiveView,
      src: publicAssets('shared/interactive_avatar.webp'),
      title: t('labels.interactiveView'),
      description: t('labels.360view'),
    });
  }

  if (projectUtils.hasTimeline(project)) {
    tabs.push({
      key: ELandingTabsEnum.Timeline,
      src: publicAssets('shared/timeline_avatar.webp'),
      title: t('labels.timeline'),
      description: t('labels.progressMap'),
    });
  }

  return tabs;
};

export const timelineSteps = [
  {
    title: 'תכנון ראשוני',
    description: 'תוכנית ראשונית מוכנה.',
    date: 'מרץ 2024',
    iconSrc: 'https://picsum.photos/50/50',
    status: ETimelineStepStatus.Completed,
    width: 300,
  },
  {
    title: 'תכנון ראשוני',
    description: 'תוכנית ראשונית מוכנה.',
    date: 'מרץ 2024',
    iconSrc: 'https://picsum.photos/50/50',
    status: ETimelineStepStatus.Completed,
    width: 300,
  },
  {
    title: 'תכנון ראשוני',
    description: 'תוכנית ראשונית מוכנה.',
    date: 'מרץ 2024',
    iconSrc: 'https://picsum.photos/50/50',
    status: ETimelineStepStatus.Completed,
    width: 300,
  },
  {
    title: 'הכנת תכנית',
    description: 'תוכנית מוכנה לאישור.',
    date: 'יוני 2024',
    iconSrc: 'https://picsum.photos/50/50',
    status: ETimelineStepStatus.Completed,
    width: 300,
  },
  {
    title: 'בניית תוכנית',
    description: 'תוכנית מוכנה לבנייה.',
    date: 'אוקטובר 2024',
    iconSrc: 'https://picsum.photos/50/50',
    status: ETimelineStepStatus.Completed,
    width: 250,
  },
  {
    title: 'בניית תוכנית',
    description: 'תוכנית מוכנה לבנייה.',
    date: 'נובמבר 2024',
    status: ETimelineStepStatus.Upcoming,
    avatar: featherAvatar,
    width: 300,
  },
  {
    title: 'בניית תוכנית111',
    description: 'תוכנית מוכנה לבנייה.',
    date: 'ספטמבר 2024',
    status: ETimelineStepStatus.Upcoming,
    avatar: starAvatar,
    width: 300,
  },
];
